import React, {useEffect, useState, useMemo} from "react";
import {useViewerStore} from "../../../../../../../store/store";
import {Button, Row, Col} from "antd";
import {runGhostScenariosAndSave} from "../../../../../../../services/aiModuleFillingService";
import treeCartService from "../../../../../../../services/treeCartService";
import type {TreeGroup} from "../../../../../../../@types/types";

/** Minimum production gain % to consider a scenario "worth it" */
const MIN_GAIN_PCT = 0.1;
/** Minimum gain-per-$1000 (gain % / (cost/1000)) to consider worth it when cost > 0 */
const MIN_GAIN_PER_1K = 0.05;

export const CutScenariosTable: React.FC = () => {
    const sceneViewer = useViewerStore((s) => s.sceneViewer);
    const aiModuleFillingScenarios = useViewerStore((s) => s.aiModuleFillingScenarios) ?? [];
    const actualWattage = useViewerStore((s) => s.actualWattage) ?? 0;
    const multiplier = useViewerStore((s) => s.multiplier) ?? 0;
    const aiModuleFillingLoading = useViewerStore((s) => s.aiModuleFillingLoading);

    const [cutCostMap, setCutCostMap] = useState<Record<string, number | null>>({});
    const [loadingCosts, setLoadingCosts] = useState(false);
    const [onlyWorthIt, setOnlyWorthIt] = useState(true);

    const baseScenario = aiModuleFillingScenarios[0];
    const baseWattage = baseScenario?.actualWattage ?? actualWattage;
    const baseMult = baseScenario?.systemMultiplier ?? multiplier;

    useEffect(() => {
        const trees = sceneViewer?.treeGroup?.children as TreeGroup[] | undefined;
        if (!trees?.length) return;
        let cancelled = false;
        setLoadingCosts(true);
        (async () => {
            const token = await treeCartService.getExcelServiceToken();
            if (typeof token !== "string" || cancelled) {
                setLoadingCosts(false);
                return;
            }
            const map: Record<string, number | null> = {};
            for (const tree of trees) {
                const treeId = tree.userData?.treeId;
                if (!treeId) continue;
                const cost = await treeCartService.getTreeCutCostWithDefaults(token, tree);
                if (cancelled) return;
                map[treeId] = cost;
            }
            if (!cancelled) {
                setCutCostMap(map);
                setLoadingCosts(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [sceneViewer?.treeGroup?.children?.length]);

    const singleTreeScenarios = useMemo(() => {
        if (!baseWattage || baseWattage <= 0) return [];
        return aiModuleFillingScenarios.slice(1).map((s) => {
            const gainWattage = (s.actualWattage ?? 0) - baseWattage;
            const gainPct = baseWattage ? (gainWattage / baseWattage) * 100 : 0;
            const cost = s.cutCost ?? cutCostMap[s.treeId ?? ""] ?? null;
            const costNum = cost != null ? Number(cost) : NaN;
            const worthIt =
                gainPct >= MIN_GAIN_PCT &&
                (costNum === 0 || !Number.isFinite(costNum) || gainPct / (costNum / 1000) >= MIN_GAIN_PER_1K);
            return {
                ...s,
                gainWattage,
                gainPct,
                cost: costNum,
                worthIt,
            };
        });
    }, [aiModuleFillingScenarios, baseWattage, cutCostMap]);

    const multiTreeApprox = useMemo(() => {
        const trees = (sceneViewer?.treeGroup?.children ?? []) as TreeGroup[];
        if (trees.length < 2 || !baseMult) return [];
        const withImpact = trees
            .map((t) => ({
                treeId: t.userData?.treeId,
                treeImpact: t.treeImpact ?? 0,
                cost: cutCostMap[t.userData?.treeId ?? ""] ?? null,
            }))
            .filter((t) => t.treeId && (t.treeImpact > 0 || (t.cost != null && Number(t.cost) > 0)));
        const combos: { treeIds: string[]; totalImpact: number; totalCost: number; gainPctApprox: number; worthIt: boolean }[] = [];
        for (let i = 0; i < withImpact.length; i++) {
            for (let j = i + 1; j < withImpact.length; j++) {
                const a = withImpact[i];
                const b = withImpact[j];
                const totalImpact = a.treeImpact + b.treeImpact;
                const totalCost = (a.cost != null ? Number(a.cost) : 0) + (b.cost != null ? Number(b.cost) : 0);
                const gainPctApprox = baseMult ? (totalImpact / baseMult) * 100 : 0;
                const worthIt =
                    gainPctApprox >= MIN_GAIN_PCT &&
                    (totalCost === 0 || gainPctApprox / (totalCost / 1000) >= MIN_GAIN_PER_1K);
                combos.push({
                    treeIds: [a.treeId!, b.treeId!],
                    totalImpact,
                    totalCost,
                    gainPctApprox,
                    worthIt,
                });
            }
        }
        combos.sort((x, y) => y.gainPctApprox - x.gainPctApprox);
        return combos;
    }, [sceneViewer?.treeGroup?.children, baseMult, cutCostMap]);

    const filteredSingle = onlyWorthIt ? singleTreeScenarios.filter((s) => s.worthIt) : singleTreeScenarios;
    const filteredMulti = onlyWorthIt ? multiTreeApprox.filter((m) => m.worthIt) : multiTreeApprox;

    const runScenarios = () => {
        runGhostScenariosAndSave();
    };

    return (
        <div className={"tab-content-container"}>
            <div className={"tab-content-sticky-container"}>
                <Row className="mb-1" style={{alignItems: "center", flexWrap: "wrap", gap: 8}}>
                    <Col>
                        <span style={{fontSize: "1rem"}}>
                            Base production: <b>{Math.round(baseWattage).toLocaleString()} W</b>
                            {baseMult ? ` (mult. ${baseMult.toFixed(4)})` : ""}
                        </span>
                    </Col>
                    <Col>
                        <Button
                            type="primary"
                            size="small"
                            onClick={runScenarios}
                            loading={aiModuleFillingLoading}
                            disabled={!sceneViewer?.treeGroup?.children?.length}
                        >
                            {aiModuleFillingScenarios.length > 0 ? "Refresh scenarios" : "Calculate scenarios"}
                        </Button>
                    </Col>
                    <Col>
                        <label style={{fontSize: "0.9rem", cursor: "pointer"}}>
                            <input
                                type="checkbox"
                                checked={onlyWorthIt}
                                onChange={(e) => setOnlyWorthIt(e.target.checked)}
                            />
                            {" "}Only show worth-it (balanced cost vs gain)
                        </label>
                    </Col>
                </Row>
            </div>

            <div className={"tab-content-scrollable-container"} style={{paddingTop: 8}}>
                {aiModuleFillingScenarios.length === 0 && !aiModuleFillingLoading && (
                    <p style={{color: "var(--muted)", marginBottom: 12}}>
                        Run &quot;Calculate scenarios&quot; to see single-tree cut options with production gain and cost.
                    </p>
                )}

                {singleTreeScenarios.length > 0 && (
                    <section style={{marginBottom: 20}}>
                        <h4 style={{marginBottom: 8, fontSize: "1rem"}}>Single-tree cut scenarios</h4>
                        <table className={"tab__table"}>
                            <thead>
                                <tr>
                                    <th>Cut tree</th>
                                    <th>Cut cost ($)</th>
                                    <th>Est. production (W)</th>
                                    <th>Gain %</th>
                                    <th>Worth it?</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredSingle.map((s, i) => (
                                    <tr key={s.treeId ?? i}>
                                        <td>{s.treeLabel ?? s.treeId ?? "—"}</td>
                                        <td>{s.cost != null && Number.isFinite(s.cost) ? `$${Math.round(s.cost)}` : (loadingCosts ? "…" : "—")}</td>
                                        <td>{Math.round(s.actualWattage ?? 0).toLocaleString()}</td>
                                        <td style={{color: s.gainPct >= 0 ? "var(--success, #52c41a)" : undefined}}>
                                            {s.gainPct >= 0 ? "+" : ""}{s.gainPct.toFixed(2)}%
                                        </td>
                                        <td>{s.worthIt ? "✓" : "—"}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {onlyWorthIt && filteredSingle.length === 0 && singleTreeScenarios.length > 0 && (
                            <p style={{fontSize: 12, color: "var(--muted)"}}>No scenarios pass the worth-it filter. Uncheck the filter to see all.</p>
                        )}
                    </section>
                )}

                {multiTreeApprox.length > 0 && (
                    <section>
                        <h4 style={{marginBottom: 8, fontSize: "1rem"}}>Multi-tree cut (approximate)</h4>
                        <p style={{fontSize: 11, color: "var(--muted)", marginBottom: 6}}>
                            Approximate gain from sum of tree impacts; cost = sum of individual tree cut costs.
                        </p>
                        <table className={"tab__table"}>
                            <thead>
                                <tr>
                                    <th>Cut trees</th>
                                    <th>Total cost ($)</th>
                                    <th>Est. gain % (approx)</th>
                                    <th>Worth it?</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredMulti.map((m, i) => (
                                    <tr key={i}>
                                        <td>{m.treeIds.join(", ")}</td>
                                        <td>${Math.round(m.totalCost)}</td>
                                        <td style={{color: m.gainPctApprox >= 0 ? "var(--success, #52c41a)" : undefined}}>
                                            +{m.gainPctApprox.toFixed(2)}%
                                        </td>
                                        <td>{m.worthIt ? "✓" : "—"}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {onlyWorthIt && filteredMulti.length === 0 && multiTreeApprox.length > 0 && (
                            <p style={{fontSize: 12, color: "var(--muted)"}}>No multi-tree combos pass the worth-it filter.</p>
                        )}
                    </section>
                )}
            </div>
        </div>
    );
};

export default CutScenariosTable;
